import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";
import { useCallback, useRef, useState } from "react";
import { getErrorMessage } from "../utils/errorMessages";

export function useResendInvitation() {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase.rpc("resend_invitation", {
        p_invitation_id: invitationId,
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      toast.success("Invitation resent successfully");
      setResendingId(null);
    },
    onError: (error: Error) => {
      const message = getErrorMessage(error);
      toast.error(message);
      setResendingId(null);
    },
  });

  // Debounced mutate function to prevent spam clicking
  const resendInvitation = useCallback(
    (invitationId: string) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set resending state immediately
      setResendingId(invitationId);

      // Set new timer for 500ms
      debounceTimerRef.current = setTimeout(() => {
        mutation.mutate(invitationId);
      }, 500);
    },
    [mutation],
  );

  return {
    resendInvitation,
    isResending: (invitationId: string) => resendingId === invitationId,
    isPending: mutation.isPending,
  };
}
